<template>
  <div class="w-full flex flex-col p-8 text-left space-y-10 font-sans">
    <!-- Entity Details -->
    <section>
      <h2 class="text-2xl font-semibold mb-4 border-b pb-2">Basic Details</h2>
      <div class="flex items-start flex-wrap gap-x-6 mb-6">
        <img :src="getCompanyLogo" alt="Company Logo" class="w-24 h-24 object-contain rounded border border-gray-300" />
        <div class="space-y-1 text-gray-800">
          <h3 class="text-xl font-bold">{{ item.en_company_name }} ({{ item.company_short_name }})</h3>
          <p class="text-gray-600 italic">{{ item.ar_company_name }}</p>
          <p><strong>Entity Industry:</strong> {{ item.entity_industry }}</p>
          <p><strong>Mother Company:</strong> {{ item.mother_company_name || 'N/A' }}</p>
          <p><strong>Company Size:</strong> {{ item.company_size }}</p>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-gray-700">
        <div><strong>Trade License Number:</strong> {{ item.trade_license_number }}</div>
        <div><strong>Trade License Authority:</strong> {{ item.trade_license_number }}</div>
        <div><strong>Company Type:</strong> {{ item.company_type }}</div>
        <div><strong>City:</strong> {{ item.city }}</div>
        <div>
          <strong>Website: </strong>
          <a :href="item.website" target="_blank" class="text-blue-600 hover:underline">
            {{ item.website }}
          </a>
        </div>
        <div class="flex">
          <strong>Entity of Interest:</strong>
          <div class="ml-1">{{ getEntityName(item.entity) }}</div>
          <div class="ml-4" title="Edit">
            <icon-pen @click.stop="editEmployerDetails" class="w-5 h-5 cursor-pointer hover:text-blue-600"></icon-pen>
          </div>
        </div>
        <div class="md:col-span-2">
          <div class="flex">
            <strong>Commercial Activity:</strong>
            <div class="ml-1 whitespace-pre-wrap">{{ stripHtml(item.commercial_activity) }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Persons -->
    <section>
      <h2 class="text-2xl font-semibold mb-4 border-b pb-2">{{ item.contact_persons.length > 1 ? 'Contact Persons'
        :
        'Contact Person' }}</h2>
      <div class="space-y-6">
        <div v-for="(contact, index) in item.contact_persons" :key="index"
          class="flex flex-col md:flex-row items-center space-y-4 md:space-x-5 p-4 border rounded-lg shadow-sm hover:shadow-md transition">
          <img v-if="contact.avatar" :src="getAvatar(contact.avatar)" alt="Personal Photo"
            class="w-24 xs:w-28 sm:w-32 md:w-20 lg:w-24 h-24 xs:h-28 sm:h-32 md:h-20 lg:h-24 object-cover rounded-full border border-gray-300" />
          <img v-else src="../../assets/images/default/avatar.jpg" alt="Personal Photo"
            class="w-24 xs:w-28 sm:w-32 md:w-20 lg:w-24 xl:w-28 2xl:w-32 h-24 xs:h-28 sm:h-32 md:h-20 lg:h-24 xl:h-28 2xl:h-32 object-cover rounded-full border border-gray-300" />
          <div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-800">
            <div><strong>Name:</strong> {{ contact.first_name }} {{ contact.last_name }}</div>
            <div><strong>Designation:</strong> {{ contact.designation }}</div>
            <div>
              <strong>Mobile:</strong>
              <a :href="`tel:${contact.mobile_number}`" class="ml-1 text-blue-600 hover:underline">
                {{ contact.mobile_number }}
              </a>
            </div>
            <div>
              <strong>Landline:</strong>
              <a v-if="contact.land_line_number" :href="`tel:${contact.land_line_number}`"
                class="ml-1 text-blue-600 hover:underline">
                {{ contact.land_line_number }}
              </a>
              <span v-else class="ml-1">N/A</span>
            </div>
            <div class="flex flex-wrap">
              <strong>Email:</strong>
              <a :href="`mailto:${contact.email}`" class="ml-1 text-blue-600 hover:underline">
                {{ contact.email }}
              </a>
              <div v-if="contact.user" class="ml-1">
                <div v-if="contact.user.email_verification_token" class="flex">
                  <div class="mr-4">
                    (<span class="text-red-600">Not Verified</span>)
                  </div>
                  <div @click.stop="verifyEmailByAdmin"
                    class="flex border border-green-600 bg-white hover:bg-green-600 text-green-600 hover:text-white cursor-pointer px-2 py-1 text-xs font-medium rounded-lg">
                    Verify
                  </div>
                </div>
                <div v-else>(<span class="text-green-600">Verified</span>)</div>
              </div>
              <div v-else class="ml-1">
                <div class="flex">
                  <div class="mr-4">
                    (<span class="text-red-600">Not Approved</span>)
                  </div>
                  <div @click.stop="approvePartnerUser(contact.token)"
                    class="flex border border-green-600 bg-white hover:bg-green-600 text-green-600 hover:text-white cursor-pointer px-2 py-1 text-xs font-medium rounded-lg">
                    Approve
                  </div>
                </div>
              </div>
            </div>
            <div><strong>Username:</strong> {{ contact.username }}</div>
          </div>
        </div>
      </div>
    </section>


    <section v-if="item.chart_file != '' || item.trading_license_file != ''">
      <h2 class="text-2xl font-semibold mb-4 border-b pb-2">Documents</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col items-center space-y-2 p-4 border rounded-lg shadow-sm hover:shadow-md transition">
          <div class="font-bold">CHART</div>
          <div class="w-full overflow-x-hidden flex flex-col items-center text-center space-y-2">
            <div class="h-20 w-20 border flex flex-col justify-center items-center">
              <img class="h-16 w-16" src="../../assets/images/partner/chart.png" alt="Chart">
            </div>
            <div v-if="getDoc1">
              <a :href="getDoc1" class="hover:underline text-blue-600" target="_blank">View</a>
            </div>
            <div v-else>No CHART Uploaded</div>
            <div v-if="chartUpdateEnabled" class="w-full flex flex-col">
              <div v-if="chartFile.val"
                class="flex items-center gap-3 p-3 border border-gray-200 rounded-lg shadow-sm bg-white w-fit mb-2">
                <div class="text-blue-500">
                  <icon-document class="h-6 w-6"></icon-document>
                </div>
                <div class="text-sm font-medium text-gray-700 truncate max-w-xs">
                  {{ chartFile.val.name }}
                </div>
                <button class="flex-1 flex justify-end ml-3 text-gray-400 hover:text-red-500"
                  @click="cancelFileUpload('chartFile')">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="flex justify-center items-center space-x-2">
                <div v-show="!chartFile.val" class="text-sm leading-5 flex">
                  <input ref="chartFile" class="hidden" id="chartFile" @change="handleChartFile" type="file"
                    accept="image/*,application/pdf">
                  <div @click.stop="onPickFileChartFile"
                    class="rounded-lg bg-ternary text-white px-4 py-1 text-sm uppercase cursor-pointer flex items-center justify-center">
                    Upload
                  </div>
                </div>
                <button @click="submitCommonUpdate('chartFile')"
                  class="bg-blue-600 text-white px-2 py-1 rounded-lg text-xs">Submit</button>
                <button @click="cancelFileUpload('chartFile')"
                  class="bg-red-600 text-white px-2 py-1 rounded-lg text-xs">Cancel</button>
              </div>
              <transition name="form-fade" mode="out-in">
                <p v-if="!chartFile.isValid" class="h-4 text-xs text-red-600 mt-1">
                  {{ chartFile.msg }}
                </p>
              </transition>
            </div>
            <div v-else @click="chartUpdateEnabled = true" class="hover:underline cursor-pointer text-gray-600 italic">
              Update Chart ?
            </div>
          </div>
        </div>
        <div class="flex flex-col items-center space-y-2 p-4 border rounded-lg shadow-sm hover:shadow-md transition">
          <div class="font-bold">TRADING LICENSE</div>
          <div class="w-full overflow-x-hidden flex flex-col items-center text-center space-y-2">
            <div class="h-20 w-20 border flex flex-col justify-center items-center">
              <img class="h-16" src="../../assets/images/partner/trading_license.png" alt="Trading License">
            </div>
            <div v-if="getDoc2">
              <a :href="getDoc2" class="hover:underline text-blue-600" target="_blank">View</a>
            </div>
            <div v-else>No TRADING LICENSE Uploaded</div>
            <div v-if="tradingLicenseUpdateEnabled" class="w-full flex flex-col">
              <div v-if="tradingLicenseFile.val"
                class="flex items-center gap-3 p-3 border border-gray-200 rounded-lg shadow-sm bg-white w-fit mb-2">
                <div class="text-blue-500">
                  <icon-document class="h-6 w-6"></icon-document>
                </div>
                <div class="text-sm font-medium text-gray-700 truncate max-w-xs">
                  {{ tradingLicenseFile.val.name }}
                </div>
                <button class="flex-1 flex justify-end ml-3 text-gray-400 hover:text-red-500"
                  @click="cancelFileUpload('tradingLicenseFile')">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="flex justify-center items-center space-x-2">
                <div v-show="!tradingLicenseFile.val" class="text-sm leading-5 flex">
                  <input ref="tradingLicenseFile" class="hidden" id="tradingLicenseFile"
                    @change="handleTradingLicenseFile" type="file" accept="image/*,application/pdf">
                  <div @click.stop="onPickTradingLicenseFile"
                    class="rounded-lg bg-ternary text-white px-4 py-1 text-sm uppercase cursor-pointer flex items-center justify-center">
                    Upload
                  </div>
                </div>
                <button @click="submitCommonUpdate('tradingLicenseFile')"
                  class="bg-blue-600 text-white px-2 py-1 rounded-lg text-xs">Submit</button>
                <button @click="cancelFileUpload('tradingLicenseFile')"
                  class="bg-red-600 text-white px-2 py-1 rounded-lg text-xs">Cancel</button>
              </div>
              <transition name="form-fade" mode="out-in">
                <p v-if="!tradingLicenseFile.isValid" class="h-4 text-xs text-red-600 mt-1">
                  {{ tradingLicenseFile.msg }}
                </p>
              </transition>
            </div>
            <div v-else @click="tradingLicenseUpdateEnabled = true"
              class="hover:underline cursor-pointer text-gray-600 italic">
              Update Trading License ?
            </div>
          </div>
        </div>
      </div>
    </section>
    <div v-if="title == 'Review & Verify Employer Details' && hasPermission('verify-partner')"
      class="flex justify-center mt-8">
      <div @click.stop="verifyMe"
        class="border-2 border-ternary w-28 flex justify-center py-2 shadow-lg bg-ternary hover:bg-white text-white rounded-lg font-bold cursor-pointer hover:text-ternary">
        Verify
      </div>
    </div>
    <div class="h-full flex justify-center items-center" v-if="isSwitching">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>

    <transition name="fade" mode="out-in">
      <base-modal v-if="isEdit" :title="`Edit Employer Details`" @close="closeEditModal">
        <div class="flex flex-col px-8">
          <div class="flex flex-col" :class="{ invalid: !entity.isValid }">
            <div class="flex justify-start mt-4">
              <label for="entity" class="mr-4">Entity of interest *</label>
              <div class="flex flex-col">
                <label v-for="(entity, index) in entityList" :key="index">
                  <input type="checkbox" v-model="entities" :value="entity" @blur="clearValidity('entity')"
                    class="mr-2">{{
                      getEntityName(entity) }}
                </label>
              </div>
            </div>
            <transition name="form-fade" mode="out-in">
              <p v-if="!entity.isValid" class="h-4 text-xs text-red-600">
                {{ entity.msg }}
              </p>
            </transition>
          </div>
          <div class="flex justify-end">
            <div @click.stop="submitForm"
              class="border-2 font-medium border-ternary text-white hover:text-ternary bg-ternary hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">
              Update</div>
          </div>
        </div>
      </base-modal>
    </transition>

    <div class="h-full flex justify-center items-center" v-if="isLoading">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>

  </div>
</template>

<script>
import gql from "graphql-tag";
import IconPen from '../../components/icons/IconPen.vue';
import IconDocument from '../../components/icons/IconDocument.vue';
import { getPartnerFileDownloadUrl } from '@/helpers/utils.js';
import { fileSecurityValidator } from '@/helpers/fileSecurity';
export default {
  name: "partner-view",
  props: ["item", "title"],
  emits: ["verify-me", "update-employer-details", "close"],
  components: {
    IconPen,
    IconDocument,
  },
  data() {
    return {
      isSwitching: false,
      chartUpdateEnabled: false,
      tradingLicenseUpdateEnabled: false,
      formIsValid: true,
      chartFile: {
        val: null,
        isValid: true,
        msg: "",
      },
      tradingLicenseFile: {
        val: null,
        isValid: true,
        msg: "",
      },
      isEdit: false,
      entityList: ["ATHS", "ADPOLY", "FCHS", "ADVETI"],
      entities: [],
      entity: {
        val: "",
        isValid: true,
        msg: "",
      },
      isLoading: false,
    };
  },
  watch: {
    'chartFile.val': function () {
      this.chartFile.isValid = true;
      this.chartFile.msg = ''
    },
    'tradingLicenseFile.val': function () {
      this.tradingLicenseFile.isValid = true;
      this.tradingLicenseFile.msg = ''
    },
  },
  computed: {
    role() {
      return this.$store.getters.role;
    },
    getCompanyLogo() {
      return process.env.VUE_APP_PUBLIC_URL + this.item.company_logo;
    },
    getDoc1() {
      if (this.item.chart_file) {
        return getPartnerFileDownloadUrl(this.item.chart_file);
      } else {
        return "";
      }
    },
    getDoc2() {
      if (this.item.trading_license_file) {
        return getPartnerFileDownloadUrl(this.item.trading_license_file);
      } else {
        return "";
      }
    },
  },
  methods: {
    stripHtml(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },
    getAvatar(imgUrl) {
      return process.env.VUE_APP_PUBLIC_URL + imgUrl;
    },
    getEntityName(entity) {
      return entity.replace(/ATHS/g, "ATS");
    },
    closeComponent() {
      this.$emit('trigger-parent');
    },
    formattedDate(date) {
      if (date) {
        const parsedDate = new Date(date);
        return parsedDate
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "-");
      } else {
        return "";
      }
    },
    async verifyEmailByAdmin() {
      const VERIFY_EMAIL_BY_ADMIN_MUTATION = gql`
        mutation VerifyEmailByAdmin($email_verification_token: String!) {
          verifyEmailByAdmin(
            email_verification_token: $email_verification_token
          ) {
            error
            message
          }
        }
      `;

      this.isSwitching = true;
      let error_msg = null;
      let success_msg = null;

      try {
        await this.$apollo
          .mutate({
            mutation: VERIFY_EMAIL_BY_ADMIN_MUTATION,
            variables: {
              email_verification_token: this.item.user.email_verification_token,
            },
          })
          .then((data) => {
            if (data.data.verifyEmailByAdmin.error) {
              error_msg = data.data.verifyEmailByAdmin.message;
            } else {
              success_msg = data.data.verifyEmailByAdmin.message;
            }
          })
          .catch((error) => {
            console.log(error);
            error_msg = error;
          });
      } catch (error) {
        console.log(error);
        error_msg = error.message || "Failed to authenticate, try later.";
      }

      if (error_msg === null) {
        this.$swal.fire({
          icon: "success",
          title: "Success",
          text: success_msg,
        }).then(() => {
          this.$emit('close');
        });
      } else {
        this.$swal.fire({
          icon: "error",
          title: "Oops...",
          text: error_msg,
        });
      }

      this.isSwitching = false;
    },
    async approvePartnerUser(token) {
      console.log(token);
    },
    // cancel file uploads dynamically..
    cancelFileUpload(input) {
      if (this[input] == this.chartFile) {
        this.chartUpdateEnabled = false;
        this.$refs.chartFile.value = null;
      }
      if (this[input] == this.tradingLicenseFile) {
        this.tradingLicenseUpdateEnabled = false;
        this.$refs.tradingLicenseFile.value = null;
      }
      this[input].val = null;
      this[input].isValid = true;
      this[input].msg = "";
    },

    // for chart picker
    onPickFileChartFile() {
      this.$refs.chartFile.click();
    },

    // for license picker
    onPickTradingLicenseFile() {
      this.$refs.tradingLicenseFile.click();
    },
    // for saving the file to memory for trading file
    async handleTradingLicenseFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.tradingLicenseFile.msg = "Validating file security...";
      try {
        const validation = await fileSecurityValidator.validateFileWithSize(file, "images_and_pdf", 2);
        if (!validation.valid) {
          this.tradingLicenseFile.isValid = false;
          this.tradingLicenseFile.msg = validation.message;
          this.tradingLicenseFile.val = null;
          event.target.value = "";
          this.$swal.fire({
            icon: "error",
            title: "Security Alert: " + validation.attackType,
            text: validation.message,
          });
          return;
        }
        this.tradingLicenseFile.val = file;
        this.tradingLicenseFile.isValid = true;
        this.tradingLicenseFile.msg = "";
      } catch (e) {
        this.tradingLicenseFile.isValid = false;
        this.tradingLicenseFile.msg = "File validation failed";
        this.tradingLicenseFile.val = null;
        event.target.value = "";
      }
    },

    // for saving the file to the memory for chart file
    async handleChartFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.chartFile.msg = "Validating file security...";
      try {
        const validation = await fileSecurityValidator.validateFileWithSize(file, "images_and_pdf", 2);
        if (!validation.valid) {
          this.chartFile.isValid = false;
          this.chartFile.msg = validation.message;
          this.chartFile.val = null;
          event.target.value = "";
          this.$swal.fire({
            icon: "error",
            title: "Security Alert: " + validation.attackType,
            text: validation.message,
          });
          return;
        }
        this.chartFile.val = file;
        this.chartFile.isValid = true;
        this.chartFile.msg = "";
      } catch (e) {
        this.chartFile.isValid = false;
        this.chartFile.msg = "File validation failed";
        this.chartFile.val = null;
        event.target.value = "";
      }
    },

    async submitCommonUpdate(fileName) {

      let isFormValid = true;

      if (fileName == 'chartFile') {
        if (this.chartUpdateEnabled) {
          if (this.chartFile.val == null) {
            this.formIsValid = false;
            this.chartFile.isValid = false;
            this.chartFile.msg = "You must upload the CHART.";
            isFormValid = false;
          } else {
            this.chartFile.isValid = true;
          }
        }
      } else if (fileName == 'tradingLicenseFile') {
        if (this.tradingLicenseUpdateEnabled) {
          if (this.tradingLicenseFile.val == null) {
            this.formIsValid = false;
            this.tradingLicenseFile.isValid = false;
            this.tradingLicenseFile.msg = "You must upload the TRADING LICENSE.";
            isFormValid = false;
          } else {
            this.tradingLicenseFile.isValid = true;
          }
        }
      } else {
        return;
      }

      if (!isFormValid) {
        return;
      }

      this.isLoading = true;
      let error_msg = null;
      let success_msg = null;

      try {
        const UPDATE_PARTNER_ATTACHMENTS = gql`
          mutation UpdatePartnerAttachments($input: PartnerAttachmentUpdateInput!) {
              updatePartnerAttachments(input: $input) {
                error
                message
              }
            }
            `;

        await this.$apollo.mutate({
          mutation: UPDATE_PARTNER_ATTACHMENTS,
          variables: {
            input: {
              id: parseInt(this.item.id),
              chart_file: fileName == 'chartFile' ? this.chartFile.val : null,
              trading_license_file: fileName == 'tradingLicenseFile' ? this.tradingLicenseFile.val : null,
            }
          },
        }).then((data) => {
          if (data.data.updatePartnerAttachments.error) {
            error_msg = data.data.updatePartnerAttachments.message;
          } else {
            success_msg = data.data.updatePartnerAttachments.message;
          }
        }).catch((error) => {
          console.error(error)
        });

      } catch (error) {
        error_msg = error.message || "Failed to authenticate, try later.";
      }

      this.isLoading = false;

      // clear the form
      if (this.chartUpdateEnabled) {
        this.chartUpdateEnabled = false;
        this.chartFile = {
          val: null,
          isValid: true,
          msg: "",
        };
        this.$refs.chartFile.value = null;
      }
      if (this.tradingLicenseUpdateEnabled) {
        this.tradingLicenseUpdateEnabled = false;
        this.tradingLicenseFile = {
          val: null,
          isValid: true,
          msg: "",
        };
        this.$refs.tradingLicenseFile.value = null;
      }

      if (error_msg === null) {
        this.$swal.fire({
          icon: "success",
          title: "Success",
          text: success_msg,
        }).then(() => {
          this.$emit('update-employer-details');
        });
      } else {
        this.$swal.fire({
          icon: "error",
          title: "Error",
          text: error_msg,
        });
      }

    },
    verifyMe() {
      this.$swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, verify it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          this.$emit('verify-me', this.item.user.id);
        }
      })
    },
    editEmployerDetails() {
      this.entities = this.item.entity.split(",");
      this.isEdit = true;
    },
    closeEditModal() {
      this.isEdit = false;
      this.entities = [];
    },
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    validateForm() {
      this.formIsValid = true;

      if (this.entities.length <= 0) {
        this.entity.isValid = false;
        this.entity.msg = "You must select any of the option.";
        this.formIsValid = false;
      } else {
        this.entity.val = this.entities.join(',');
      }

    },
    async submitForm() {
      this.validateForm();

      if (!this.formIsValid) {
        return;
      }

      const formData = {
        entity: this.entity.val,
      }

      const UPDATE_MUTATION = gql`
          mutation EditEmployerDetails($id: Int!, $input: UpdatePartnerFormInput!) {
              editEmployerDetails(id: $id, input: $input) {
                  error
                  message
              }
          }
      `;

      this.isLoading = true;
      let error_msg = null;
      let success_msg = null;

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_MUTATION,
          variables: {
            id: parseInt(this.item.id),
            input: formData
          }
        }).then((data) => {
          if (data.data.editEmployerDetails.error) {
            error_msg = data.data.editEmployerDetails.message;
          } else {
            success_msg = data.data.editEmployerDetails.message;
          }
        }).catch((error) => {
          error_msg = error;
        })

      } catch (error) {
        error_msg = error.message || "Failed to authenticate, try later.";
      }

      this.isLoading = false;

      if (error_msg == null) {
        this.$emit('update-employer-details');
        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer)
            toast.addEventListener('mouseleave', this.$swal.resumeTimer)
          }
        })
        Toast.fire({
          icon: 'success',
          title: success_msg
        }).then((result) => {
          if (result.isDismissed) {
            this.closeEditModal();
          }
        })
      } else {
        this.$swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: error_msg,
        })
      }
    },
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.5s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
  opacity: 0;
  height: 0px;
}
</style>
